import {
    Button,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useState } from 'react';
import OrganizationService from './OrganizationService';
import { useGlobalStore } from '../../../application/store/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NavigationRoutes } from '../../../application/router/routes';

const UpdateTenantOrganization = () => {
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        organizationType: '',
    });
    const [fetching, setFetching] = useState(true);
    const organizationService = new OrganizationService();
    const navigate = useNavigate();
    const setMessage = useGlobalStore((state) => state.setMessage);
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    // const parentId = searchParams.get('parentId');
    // const parentName = searchParams.get('parentName');
    // const nrOfChildOrgs = searchParams.get('childOrgs')

    useEffect(() => {
        const fetchOrganization = async () => {
            setFetching(true);
            try {
                let data = await organizationService.getOrganization(id);
                setFormData({
                    id: id,
                    name: data.name || '',
                    organizationType: data.organizationType || '',
                });
            } catch (error) {
                setMessage('Kunde inte hämta organisationen.', 'error');
            } finally {
                setFetching(false);
            }
        };
        if (id) fetchOrganization();
    }, [id, setMessage]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            const result = await organizationService.updateOrganization({
                ...formData,
            });
            if (result.success) {
                setMessage('Organisationen uppdaterad!', 'success');
                navigate(NavigationRoutes.tenantOrganizations);
            } else {
                setMessage(
                    'Misslyckades med att uppdatera organisationen.',
                    'error'
                );
            }
        } catch (error) {
            setMessage('Fel vid uppdatering av organisationen.', 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            let result = await organizationService.deleteOrganization(id);
            if (result.status == 200) {
                setMessage('Organisation arkiverad.', 'success');
                navigate(NavigationRoutes.tenantOrganizations);
            }
        } catch (error) {
            //  setMessage('Misslyckades med att radera Organisationen.', 'error');
            setMessage(error.response.data.exception, 'error', 10000);
        } finally {
            setDeleteLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex-grow p-2 flex flex-col items-center">
                <CircularProgress />
            </div>
        );
    }

    const handleCancel = () => {
        navigate(NavigationRoutes.tenantOrganizations);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleCancel}
                            variant="outlined"
                            sx={{
                                borderColor: 'gray.300',
                                color: 'gray.700',
                                '&:hover': {
                                    borderColor: 'gray.400',
                                    backgroundColor: 'gray.50',
                                },
                            }}
                        >
                            Tillbaka
                        </Button>
                        <Typography
                            variant="h4"
                            className="font-semibold text-gray-900"
                        >
                            Uppdatera Organisation
                        </Typography>
                        <div></div>
                        {/* Spacer for centering */}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <Paper
                    elevation={0}
                    className="bg-white rounded-lg shadow-sm border border-gray-200"
                    sx={{ p: 6 }}
                >
                    <div className="mb-6">
                        <Typography
                            variant="h5"
                            className="font-semibold text-gray-900 mb-2"
                        >
                            Organisationsinformation
                        </Typography>
                        <Typography variant="body2" className="text-gray-600">
                            Uppdatera organisationens namn och typ nedan.
                        </Typography>
                    </div>

                    <Stack spacing={4}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={3}
                        >
                            <TextField
                                label="Organisationsnamn"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label="Organisationstyp"
                                name="organizationType"
                                value={formData.organizationType}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                        </Stack>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 sm:justify-end">
                            <Button
                                startIcon={
                                    deleteLoading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <DeleteIcon />
                                    )
                                }
                                onClick={handleDelete}
                                variant="outlined"
                                size="large"
                                disabled={deleteLoading || saveLoading}
                                sx={{
                                    minWidth: 180,
                                    py: 1.5,
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    '&:hover': {
                                        borderColor: 'error.dark',
                                        backgroundColor: 'error.50',
                                        color: 'error.dark',
                                    },
                                }}
                            >
                                Arkivera Organisation
                            </Button>
                            <Button
                                startIcon={
                                    saveLoading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                                onClick={handleSave}
                                variant="contained"
                                size="large"
                                disabled={saveLoading || deleteLoading}
                                sx={{
                                    minWidth: 200,
                                    py: 1.5,
                                    backgroundColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    },
                                }}
                            >
                                Uppdatera Organisation
                            </Button>
                        </div>
                    </Stack>
                </Paper>
            </div>
        </div>
    );
};

export default UpdateTenantOrganization;
